import Button from '@components/Button';
import Image from '@components/Image';
import Modal from '@components/Modal';
import Tip from '@components/Tip';
import Title from '@components/Title';
import { $tourData } from '@stores/dataStores';
import { $sandTableMatrixMap } from '@stores/sandTableStores';
import { ImageType, type ST, type SandTableType } from '@types';
import addComponent from '@utils/addComponent';
import cn from '@utils/cn';
import flexBoxHack from '@utils/flexBoxHack';
import { useStore, useStoreMap } from 'effector-react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useImmer } from 'use-immer';
import { isSTImage, isSTMap, isSTMatrix } from '../utils/sandTableTypeCheck';

interface Props {
  type: SandTableType;
  currentId: string;
  onConfirm: (idList: string[]) => void;
  onRequestClose: () => void;
}

export function SandTableCopyToModal({
  type,
  currentId,
  onConfirm,
  onRequestClose,
}: Props) {
  const list = useStoreMap({
    store: $tourData,
    keys: [],
    fn: (state) => state.sandBox ?? [],
  });
  const matrixMap = useStore($sandTableMatrixMap);
  const [selected, setSelected] = useImmer<string[]>([]);

  const handleClick = (id: string) => {
    setSelected((draft) => {
      const index = draft.findIndex((_id) => _id === id);
      if (index > -1) {
        draft.splice(index, 1);
      } else {
        draft.push(id);
      }
    });
  };

  const renderCover = (item: ST) => {
    if (isSTMap(item)) {
      return (
        <Image
          src="/imgs/st-map-cover.fbbf4cb4fae95ae2ae2aea0afc8e56ad.png"
          type={ImageType.sysMedia}
          className="h-full w-full"
          size={225}
        />
      );
    }
    if (isSTImage(item)) {
      return (
        <Image
          src={item.mediaUrl}
          type={ImageType.media}
          className="h-full w-full"
          size={225}
        />
      );
    }
    if (isSTMatrix(item)) {
      return (
        <Image
          src={matrixMap.get(item.data)?.thumb ?? ''}
          type={ImageType.thumb}
          className="h-full w-full"
          size={225}
        />
      );
    }
  };

  const renderIcon = (item: ST) => {
    if (isSTMap(item)) {
      return (
        <Image
          src="/imgs/st-icon-map.f2e38290a1397ff7fd873e9b58eea17c.png"
          type={ImageType.sysMedia}
          style={{ width: 26, height: 16 }}
          className="absolute right-10 top-10"
        />
      );
    }
    if (isSTImage(item) || isSTMatrix(item)) {
      return (
        <Image
          src="/imgs/st-icon-img.9e0923beca04caed73cbcdeb97a468d8.png"
          type={ImageType.sysMedia}
          style={{ width: 26, height: 16 }}
          className="absolute right-10 top-10"
        />
      );
    }
  };

  const renderItem = (item: ST) => {
    const disabled = item.id === currentId || item.type !== type;
    return (
      <div
        key={item.id}
        className={cn(
          'relative mb-20 h-225 w-225 overflow-hidden rounded border',
          {
            'ring-2 ring-orange': selected.includes(item.id),
            'opacity-50': disabled,
            'cursor-pointer': !disabled,
          }
        )}
        onClick={() => {
          if (disabled) return;
          handleClick(item.id);
        }}
      >
        {renderCover(item)}
        {renderIcon(item)}
        <div className="absolute bottom-0 flex h-30 w-full items-center justify-center bg-black-black bg-opacity-50 px-10">
          <div className="truncate text-white">{item.name}</div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen
      style={{ width: 1000, height: 'calc(100% - 60px)' }}
      header={
        <div className="flex">
          <Title>选择要复制到的沙盘</Title>
          <Tip className="ml-10 self-end text-orange">
            注：只能复制到同类型沙盘中（不包括当前沙盘）
          </Tip>
        </div>
      }
      footer={
        <Button
          disabled={selected.length === 0}
          onClick={() => onConfirm(selected)}
        >
          确认操作
        </Button>
      }
      onRequestClose={onRequestClose}
    >
      <Scrollbars>
        <div className="mt-20 flex flex-wrap justify-between px-2">
          {list.map(renderItem)}
          {flexBoxHack(225, 4)}
        </div>
      </Scrollbars>
    </Modal>
  );
}

export function showSandTableCopyToModal(
  params: Omit<Props, 'onConfirm' | 'onRequestClose'>
) {
  return new Promise<{ idList?: string[] }>((resolve) => {
    addComponent((remove) => {
      return (
        <SandTableCopyToModal
          {...params}
          onConfirm={(idList) => {
            remove();
            resolve({ idList });
          }}
          onRequestClose={() => {
            resolve({});
            remove();
          }}
        />
      );
    });
  });
}
